import { Skeleton } from 'antd';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import Link from 'components/Link/Link';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FulfillmentNote, Order, OrderItem, OrderOrFulfillmentNote, OrderStatus } from 'types/schema';
import media from 'utils/mediaStyle';
import utilMessages from 'utils/messages';
import { getPermissionsActive } from 'utils/permissionMap';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import messages from '../../containers/OrdersPage/messages';
import Money from 'components/Money/Money';
import SVGIcon from '../SVGIcon/SVGIcon';
import OrderIcon from './OrderIcon.svg';
import { getStatusColor } from './StatusIcon';
import noImage from './noIMG.svg';

function isFulfillmentNote(order: OrderOrFulfillmentNote): order is FulfillmentNote {
  return 'fulfillmentNoteId' in order;
}

interface OrderRowProps {
  order: OrderOrFulfillmentNote & { loading?: boolean };
  permissions: string[];
  onApproveOrder?: (data: any) => void;
  onDisapproveOrder?: (data: any) => void;
  onReOrderAll?: (data: any) => void;
  onBulkReOrder?: (data: any) => void;
  onOpenModal?: (data: FulfillmentNote) => void;
  statuses?: any;
  selectedOrderIds?: string[];
  onSelectOrder?: (orderId: string, checked: boolean) => void;
}

interface ProductImageProps {
  product: any;
  showOverlay?: boolean;
  remainingCount?: number;
}

const Icon = styled(SVGIcon)`
  width: 32px;
  height: 32px;
  margin: auto;
`;

const OrderRowHeaderWrapper = styled.div`
  background-color: #f6f6f6;
  padding: 12px;
  border-radius: 8px 8px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border: 1px solid #eaeaeb;
  border-bottom: none;
  ${media.md`
    padding: 16px;
  `};
`;

const OrderRowHeaderInfoWrapper = styled.div`
  display: flex;
  gap: 8px;
  ${media.md`
    gap: 14px;
  `};
`;

const OrderRowHeaderInfoText = styled.div`
  display: flex;
  flex-direction: column;
`;

const OrderRowHeaderInfoTitle = styled.p`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #000;
  ${media.md`
    font-size: 16px;
    line-height: 24px;
  `};
`;

const OrderRowHeaderInfoTitleNo = styled.span`
  color: #43a047;
`;

const OrderRowHeaderInfoDate = styled.p<{ type: OrderOrFulfillmentNote['__typename'] }>`
  font-size: 12px;
  font-weight: ${({ type }) => (type === 'FulfillmentNote' ? 400 : 500)};
  line-height: 16px;
  color: ${({ type }) => (type === 'FulfillmentNote' ? '#585A60' : '#343538')};
  ${media.md`
    font-size: ${({ type }) => (type === 'FulfillmentNote' ? 14 : 16)}px;
    font-weight: ${({ type }) => (type === 'FulfillmentNote' ? 400 : 500)};
    line-height: ${({ type }) => (type === 'FulfillmentNote' ? 20 : 24)}px;
  `};
`;

const OrderRowStatus = styled.p<{ status: string }>`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${({ status }) => getStatusColor(status)};
`;

const OrderRowItemWrapper = styled.div`
  border: 1px solid #eaeaeb;
  border-bottom: none;
  background-color: #fff;
`;

const OrderItemRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  gap: 8px;
  &:not(:last-child) {
    border-bottom: 1px solid #eaeaeb;
  }
  ${media.md`
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    gap: 0;
  `};
`;

const OrderItemRowId = styled.p`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #388bff;
  cursor: pointer;
  ${media.md`
    font-size: 16px;
    line-height: 24px;
  `};
`;

const OrderItemRowImgSection = styled.div`
  display: flex;
  gap: 16px;
`;

const OrderItemRowImgWrapper = styled.div`
  position: relative;
  width: 56px;
  height: 56px;
  ${media.md`
   width: 64px;
   height: 64px;
  `};
`;

const OrderItemRowImg = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 4px;
  ${media.md`
    width: 64px;
    height: 64px;
  `};
`;

const OrderItemRowImgOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-weight: 500;
  font-size: 16px;
  border-radius: 4px;
  ${media.md`
   width: 64px;
  height: 64px;
  `};
`;

const OrderRowFooter = styled.div`
  padding: 8px 16px;
  border-radius: 0 0 8px 8px;
  background-color: #fff;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
  border: 1px solid #eaeaeb;
`;

const OrderTotalTextLabel = styled.p`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #808289;
  ${media.md`
  font-size: 14px;
  line-height: 20px;
  `};
`;

const OrderTotalTextValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #343538;
  ${media.md`
    font-size: 20px;
    line-height: 28px;
  `};
`;

const OrderRowFooterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
`;

const OrderRowFooterButtonWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  ${Button} {
    min-width: 84px;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    box-shadow: none;
  }
  ${media.md`
    gap: 12px;
    ${Button} {
      min-width: 120px;
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      box-shadow: none;
    }
  `};
`;

const ProductImage = ({ product, showOverlay = false, remainingCount = 0 }: ProductImageProps) => {
  const formatAltText = (name: string) => {
    if (!name) return 'Product image';
    return name.length > 15 ? `${name.substring(0, 15)}...` : name;
  };

  return (
    <OrderItemRowImgWrapper>
      <OrderItemRowImg src={product.imageUrl || noImage} alt={formatAltText(product.name)} title={product.name} />
      {showOverlay && <OrderItemRowImgOverlay>+{remainingCount}</OrderItemRowImgOverlay>}
    </OrderItemRowImgWrapper>
  );
};

const ProductImageList = ({ items }: { items: OrderItem[] }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxImagesOnMobile = 3;
  const maxImagesOnDesktop = 4;
  const maxImages = isMobile ? maxImagesOnMobile : maxImagesOnDesktop;

  return (
    <OrderItemRowImgSection>
      {items.map((item, index) => {
        if (index < maxImages) {
          return <ProductImage product={item.product} key={item.product.name} />;
        }

        if (index === maxImages) {
          const remainingCount = items.length - maxImages - 1;
          return (
            <ProductImage
              product={item.product}
              key={item.product.name}
              showOverlay={remainingCount > 0}
              remainingCount={remainingCount}
            />
          );
        }
        return null;
      })}
    </OrderItemRowImgSection>
  );
};

const OrderHeader = ({
  item,
  statuses,
  selectedOrderIds = [],
  onSelectOrder,
}: {
  item: OrderOrFulfillmentNote;
  statuses?: any;
  selectedOrderIds?: string[];
  onSelectOrder?: (orderId: string, checked: boolean) => void;
}) => {
  const isFNNote = isFulfillmentNote(item);
  const status = isFNNote ? item.orders[0].status : item.status;
  const showCheckbox = !isFNNote && statuses === 'PENDING_APPROVAL';
  const orderId = !isFNNote ? (item as any).id : '';
  const isChecked = selectedOrderIds.includes(orderId);

  const handleCheckboxChange = (e: any) => {
    if (onSelectOrder && orderId) {
      onSelectOrder(orderId, e.target.checked);
    }
  };

  const content = (
    <OrderRowHeaderWrapper>
      <OrderRowHeaderInfoWrapper>
        {isFNNote && <Icon src={OrderIcon} />}
        <OrderRowHeaderInfoText>
          <OrderRowHeaderInfoText>
            {isFNNote && (
              <OrderRowHeaderInfoTitle>
                <FormattedMessage {...messages.fulfillmentNote} />:{' '}
                <OrderRowHeaderInfoTitleNo>{item.fulfillmentNoteShortCode}</OrderRowHeaderInfoTitleNo>
              </OrderRowHeaderInfoTitle>
            )}
            <OrderRowHeaderInfoDate type={item.__typename}>
              {showCheckbox && (
                <Checkbox style={{ marginRight: '8px' }} checked={isChecked} onChange={handleCheckboxChange} />
              )}
              <FormattedMessage {...messages.deliveryDate} />{' '}
              {isFNNote ? (
                <span>
                  {moment(item.deliveryStartDateTime).format('HH:mm')} -{' '}
                  {moment(item.deliveryEndDateTime).format('HH:mm | DD/MM/YYYY')}
                </span>
              ) : (
                <span>
                  {moment(item.deliveryTime?.start as any).format('HH:mm')} -{' '}
                  {moment(item.deliveryTime?.end as any).format('HH:mm | DD/MM/YYYY')}
                </span>
              )}
            </OrderRowHeaderInfoDate>
          </OrderRowHeaderInfoText>
        </OrderRowHeaderInfoText>
      </OrderRowHeaderInfoWrapper>
      <OrderRowStatus status={status}>
        {isFNNote ? (
          <FormattedMessage
            id={`app.containers.OrdersPage.status.${item.orders[0].statusText}`}
            defaultMessage="{status}"
            values={{ status: item.orders[0].statusText }}
          />
        ) : (
          item.statusText
        )}
      </OrderRowStatus>
    </OrderRowHeaderWrapper>
  );

  return isFNNote ? <Link to={`/phieu-giao/${item.fulfillmentNoteId}`}>{content}</Link> : content;
};

const OrderFooter = ({
  order,
  permissions,
  onApproveOrder,
  onDisapproveOrder,
  onReOrderAll,
  onBulkReOrder,
  onOpenModal,
}: {
  order: OrderOrFulfillmentNote;
  permissions: string[];
  onApproveOrder?: (data: any) => void;
  onDisapproveOrder?: (data: any) => void;
  onReOrderAll?: (data: any) => void;
  onBulkReOrder?: (data: any) => void;
  onOpenModal?: (data: FulfillmentNote) => void;
}) => {
  const isFFNote = isFulfillmentNote(order);
  const canApproveOrder = getPermissionsActive(['ORDER_APPROVE'], permissions);

  const shouldShowRateButton =
    isFFNote &&
    order.fulfillmentNoteStatus === 'DELIVERED' &&
    moment().diff(moment(order.orderDateTime), 'days') <= 7 &&
    !order.feedback?.rating;

  const handleRate = (fulfillmentNote: FulfillmentNote) => {
    if (onOpenModal) {
      onOpenModal(fulfillmentNote);
      sendGA4Event(EventName.CLICK_RATING_BUTTON, {
        ffm_id: fulfillmentNote.fulfillmentNoteId,
        ffm_shortcode: fulfillmentNote.fulfillmentNoteShortCode,
      });
    }
  };

  return (
    <OrderRowFooter>
      <OrderRowFooterContent>
        <div>
          <OrderTotalTextLabel>
            <FormattedMessage {...messages.totalOrder} /> ({isFFNote ? order.itemCount : order.items.length}{' '}
            <FormattedMessage {...utilMessages.product} />
            ): <OrderTotalTextValue><Money>{order.total}</Money></OrderTotalTextValue>
          </OrderTotalTextLabel>
        </div>
        <OrderRowFooterButtonWrapper>
          {isFFNote && (
            <>
              <Button
                size="large"
                onClick={() =>
                  onBulkReOrder && onBulkReOrder({ orderIds: order.orderIds, storeId: order.orders[0].store.id })
                }
              >
                <FormattedMessage {...messages.reOrder} />
              </Button>

              {shouldShowRateButton && (
                <Button type="primary" size="large" ghost onClick={() => handleRate(order)}>
                  <FormattedMessage {...messages.rate} />
                </Button>
              )}
            </>
          )}
          {!isFFNote && order.status === OrderStatus.PendingApproval && canApproveOrder && (
            <>
              <Button size="large" onClick={() => onDisapproveOrder && onDisapproveOrder({ orderId: order.id })}>
                <FormattedMessage {...messages.decline} />
              </Button>
              <Button
                type="primary"
                size="large"
                ghost
                onClick={() =>
                  onApproveOrder &&
                  onApproveOrder({
                    orderId: order.id,
                  })
                }
              >
                <FormattedMessage {...utilMessages.approve} />
              </Button>
            </>
          )}

          {!isFFNote && ![OrderStatus.PendingApproval].includes(order.status) && (
            <>
              <Button
                size="large"
                onClick={() => onReOrderAll && onReOrderAll({ orderId: order.id, storeId: order.store.id })}
              >
                <FormattedMessage {...messages.reOrder} />
              </Button>
            </>
          )}
        </OrderRowFooterButtonWrapper>
      </OrderRowFooterContent>
    </OrderRowFooter>
  );
};

const OrderItems = ({ order }: { order: OrderOrFulfillmentNote }) => {
  const renderOrderItems = useMemo(() => {
    if (isFulfillmentNote(order)) {
      return order.orders.map((subOrder: Order) => (
        <Link key={subOrder.id} to={`/orders/${subOrder.id}`}>
          <OrderItemRow>
            <OrderItemRowId>{subOrder.id}</OrderItemRowId>
            <ProductImageList items={subOrder.items} />
          </OrderItemRow>
        </Link>
      ));
    }

    return (
      <Link to={`/orders/${order.id}`}>
        <OrderItemRow>
          <OrderItemRowId>{order.id}</OrderItemRowId>
          {order?.items && <ProductImageList items={order.items} />}
        </OrderItemRow>
      </Link>
    );
  }, [order]);

  return <OrderRowItemWrapper>{renderOrderItems}</OrderRowItemWrapper>;
};

const SkeletonHeader = () => (
  <OrderRowHeaderWrapper>
    <OrderRowHeaderInfoWrapper>
      <Skeleton.Avatar active size={32} shape="square" />
      <OrderRowHeaderInfoText>
        <Skeleton.Input active size="small" style={{ width: 150, marginBottom: 8 }} />
        <Skeleton.Input active size="small" style={{ width: 100 }} />
      </OrderRowHeaderInfoText>
    </OrderRowHeaderInfoWrapper>
    <Skeleton.Input active size="small" style={{ width: 80 }} />
  </OrderRowHeaderWrapper>
);

const SkeletonItems = () => (
  <OrderRowItemWrapper>
    <OrderItemRow>
      <Skeleton.Input active size="small" style={{ width: 80 }} />
      <OrderItemRowImgSection>
        {[...Array(3)].map((_, index) => (
          <Skeleton.Image key={index} style={{ width: 64, height: 64, borderRadius: 4 }} active />
        ))}
      </OrderItemRowImgSection>
    </OrderItemRow>
  </OrderRowItemWrapper>
);

const SkeletonFooter = () => (
  <OrderRowFooter>
    <OrderRowFooterContent>
      <div>
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </div>
      <OrderRowFooterButtonWrapper>
        <Skeleton.Button active size="large" style={{ width: 108 }} />
        <Skeleton.Button active size="large" style={{ width: 108 }} />
      </OrderRowFooterButtonWrapper>
    </OrderRowFooterContent>
  </OrderRowFooter>
);

const OrderRowSkeleton = () => (
  <>
    <SkeletonHeader />
    <SkeletonItems />
    <SkeletonFooter />
  </>
);

const OrderRow = ({
  order,
  permissions,
  onApproveOrder,
  onDisapproveOrder,
  onReOrderAll,
  onBulkReOrder,
  onOpenModal,
  statuses,
  selectedOrderIds = [],
  onSelectOrder,
}: OrderRowProps) => {
  if (order.loading) {
    return <OrderRowSkeleton />;
  }

  return (
    <>
      <OrderHeader item={order} statuses={statuses} selectedOrderIds={selectedOrderIds} onSelectOrder={onSelectOrder} />
      <OrderItems order={order} />
      <OrderFooter
        order={order}
        permissions={permissions}
        onApproveOrder={onApproveOrder}
        onDisapproveOrder={onDisapproveOrder}
        onReOrderAll={onReOrderAll}
        onBulkReOrder={onBulkReOrder}
        onOpenModal={onOpenModal}
      />
    </>
  );
};

export default OrderRow;
